import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { featuredProducts } from '@/mocks/products';
import { useCart } from '@/context/CartContext';
import type { Product } from '@/mocks/products';

const ProductCard = ({ product, index }: { product: Product; index: number }) => {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const defaultSize = product.sizes[1] ?? product.sizes[0];

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addItem(product, defaultSize);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div
      className={`scroll-animate scroll-animate-delay-${(index % 3) + 1} bg-brand-cream rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer`}
      onClick={() => navigate(`/products/${product.id}`)}
    >
      {/* Product Image */}
      <div className="relative w-full overflow-hidden bg-brand-beige/30" style={{ aspectRatio: '1/1' }}>
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-contain p-4"
        />
        <span className="absolute top-3 right-3 bg-brand-olive text-white text-xs font-sans font-medium px-3 py-1 rounded-full">
          {product.tag}
        </span>
      </div>
      {/* Product Info */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-serif text-brand-brown-dark text-lg font-semibold">
            {product.name}
          </h3>
          <span className="text-brand-brown/50 text-xs font-sans whitespace-nowrap mt-1">
            {defaultSize.label}
          </span>
        </div>
        <p className="font-sans text-sm text-brand-olive italic mb-1">{product.benefit}</p>
        <span className="inline-block text-xs font-sans text-brand-brown/50 mb-3">{product.category}</span>

        <div className="flex items-center justify-between mb-4">
          <span className="font-serif text-lg font-bold text-brand-brown-dark">
            Rs. {defaultSize.price.toLocaleString()}
          </span>
          <span className="font-sans text-xs text-brand-brown/40">per {defaultSize.label}</span>
        </div>

        <button
          onClick={handleAddToCart}
          className={`w-full flex items-center justify-center gap-2 font-sans text-sm font-medium px-4 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
            added
              ? 'bg-brand-gold text-white border border-brand-gold'
              : 'border border-brand-olive text-brand-olive hover:bg-brand-olive hover:text-white'
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={added ? 'ri-check-line' : 'ri-shopping-basket-2-line'}></i>
          </div>
          {added ? 'Added!' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
};

const FeaturedProducts = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.scroll-animate').forEach((child) => {
              child.classList.add('visible');
            });
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.06 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-white px-4 md:px-8" ref={ref}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12 scroll-animate">
          <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium mb-3">
            Selected Products
          </span>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-brown-dark font-bold">
            Handcrafted Wellness
          </h2>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
          {featuredProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {/* View All */}
        <div className="text-center mt-10 scroll-animate">
          <a
            href="/products"
            className="inline-flex items-center gap-2 border border-brand-beige text-brand-brown hover:border-brand-gold hover:text-brand-gold font-sans text-sm font-medium px-7 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            View All Products
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
